import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import InputField from "../../../../../common/components/formFields/inputField/inputField";
import Button from "../../../../../common/components/button/Button";
import {
  IRestaurantPosFormData,
  IRestaurantPosFormProps,
} from "../../../../../models/IRestaurantPosForm";
import styles from "./RestaurantPosForm.module.scss";
import callApi from "../../../../../utils/api-client";
import { ClickAwayListener } from "@mui/base/ClickAwayListener";

// Validation schema using Yup
const validationSchema = yup.object({
  baseUrl: yup
    .string()
    .required("Restaurant POS base URL is required")
    .url("Please enter a valid URL")
    .matches(/^https?:\/\//, "URL must start with http:// or https://"),
  apiKey: yup
    .string()
    .required("API key is required")
    .min(10, "API key must be at least 10 characters"),
  secretKey: yup
    .string()
    .required("Secret key is required")
    .min(10, "Secret key must be at least 10 characters"),
  businessSearchName: yup.string(),
  business: yup
    .object()
    .required("Select Business")
    .shape({
      id: yup.string().required("Business ID is required"),
      name: yup.string().required("Business name is required"),
    }),
});

function RestaurantPosForm({
  onSubmit,
  initialData,
  isLoading = false,
  disableBusinessSelection = false,
  actionButtonLabel = "Connect POS System",
  isUpdateMode = false,
  formUpdateState,
  editBtnCallback = () => {},
}: IRestaurantPosFormProps) {
  const [inputFocused, setInputFocused] = React.useState<boolean>(false);
  const [unlinkedBusinessList, setUnlinkedBusinessList] = React.useState<
    Array<{ id: string; name: string }>
  >([]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm<IRestaurantPosFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: initialData || {
      baseUrl: "",
      apiKey: "",
      secretKey: "",
      business: {
        id: "",
        name: "",
      },
      businessSearchName: "",
    },
  });

  const handleReset = () => {
    reset();
  };

  const isFieldDisabled =
    isLoading || isSubmitting || (isUpdateMode && formUpdateState === "view");

  const onSubmitHandler = async (data: IRestaurantPosFormData) => {
    try {
      console.log("Submitting data:", data);

      const result = await onSubmit(data);
      if (result) {
        handleReset();
      }
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const SearchBuyerByName = async (searchText: string) => {
    try {
      if (searchText.trim()) {
        const getUnlinkedBusinessListByName: any = await callApi<any>(
          `admin/pos/unlinked-business?q=${searchText}&page=1`,
          {
            method: "GET",
          },
          "getUnlinkedBusinessListByName"
        );
        const getUnlinkedBusinessListByNameData = getUnlinkedBusinessListByName;

        if (
          getUnlinkedBusinessListByNameData &&
          getUnlinkedBusinessListByNameData?.status === "SUCCESS"
        ) {
          const businessData = getUnlinkedBusinessListByNameData?.data;
          if (businessData && businessData?.length > 0) {
            const updatedBusiness = businessData?.map((element: any) => {
              return {
                id: element?.business_setup_id,
                name: element?.business_name,
              };
            });
            if (updatedBusiness?.length > 0) {
              setUnlinkedBusinessList(updatedBusiness);
            }
          } else if (businessData && businessData?.length === 0) {
            setUnlinkedBusinessList([]);
          }
        }
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
      } else {
        console.error("Fetch error:", error);
      }
    }
  };

  const onBusinessSearch = (searchText: string) => {
    setValue("businessSearchName", searchText);
    SearchBuyerByName(searchText);
  };

  // const handleReset = () => {
  //   reset();
  // };

  const onChangeHandler =
    (name: keyof IRestaurantPosFormData) => (text: any) => {
      setValue(name, text);
    };

  return (
    <div className={styles.formContainer}>
      <div className={styles.formHeader}>
        <h2 className={styles.title}>Restaurant POS Configuration</h2>
        <p className={styles.subtitle}>
          Configure your Restaurant POS system integration
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmitHandler)} className={styles.form}>
        <div className={styles.FormSections}>
          <div className={styles.businessSection}>
            {!isUpdateMode && (
              <div className={styles.formGroup}>
                <ClickAwayListener
                  onClickAway={() => {
                    setInputFocused(false);
                  }}
                >
                  <div className={`${styles.input} ${styles.productNameSugg}`}>
                    <InputField
                      value={watch("businessSearchName")}
                      placeholder="Search Business Name"
                      label="Search Business Name"
                      id="businessSearchName"
                      onChange={(e: any) => onBusinessSearch(e)}
                      register={register("businessSearchName")}
                      maxWidth="100%"
                      errorMessage={errors.businessSearchName?.message}
                      autoComplete={false}
                      onFocus={() => {
                        setInputFocused(true);
                      }}
                      tabIndex={1}
                      disabled={disableBusinessSelection || isFieldDisabled}
                    />
                    {inputFocused && unlinkedBusinessList?.length > 0 && (
                      <div className={styles.productNameSuggestionsContaine}>
                        {unlinkedBusinessList?.map(
                          (each_business: any, index: number) => (
                            <div
                              key={each_business.id}
                              onClick={() => {
                                setValue("business", {
                                  id: each_business.id,
                                  name: each_business.name,
                                });
                                setInputFocused(false);
                              }}
                              className={`${styles.productSuggestionItem} ${
                                index !== unlinkedBusinessList.length - 1
                                  ? styles.seperator
                                  : ""
                              }`}
                            >
                              {each_business.name}
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                </ClickAwayListener>
              </div>
            )}
            <div className={styles.formGroup}>
              <InputField
                value={watch("business.name")}
                label="Business Name"
                placeholder="Selected Business Name"
                register={register("business.name")}
                errorMessage={errors.business?.name?.message}
                type="text"
                id="businessName"
                disabled={true}
              />
            </div>
          </div>

          <div className={styles.connectorContainer}>
            <div className={styles.connectorIcon}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17 8l4 4-4 4M7 16l-4-4 4-4M21 12H3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          <div className={styles.posConfigSection}>
            <div className={styles.formGroup}>
              <InputField
                value={watch("baseUrl")}
                label="Restaurant POS Base URL"
                placeholder="https://your-pos-system.com"
                register={register("baseUrl")}
                errorMessage={errors.baseUrl?.message}
                type="url"
                id="baseUrl"
                disabled={isFieldDisabled}
                onChange={onChangeHandler("baseUrl")}
              />
            </div>

            <div className={styles.formGroup}>
              <InputField
                value={watch("apiKey")}
                label="API Key"
                placeholder="Enter your API key"
                register={register("apiKey")}
                errorMessage={errors.apiKey?.message}
                type="password"
                id="apiKey"
                disabled={isFieldDisabled}
                onChange={onChangeHandler("apiKey")}
              />
            </div>

            <div className={styles.formGroup}>
              <InputField
                value={watch("secretKey")}
                label="Secret Key"
                placeholder="Enter your secret key"
                register={register("secretKey")}
                errorMessage={errors.secretKey?.message}
                type="password"
                id="secretKey"
                disabled={isFieldDisabled}
                onChange={onChangeHandler("secretKey")}
              />
            </div>
          </div>
        </div>

        <div className={styles.formActions}>
          <div className={styles.submitButtonContainer}>
            <Button
              type="submit"
              label={actionButtonLabel}
              handleClick={() => {}}
              disabled={isFieldDisabled}
              large={true}
              customClass={styles.submitButton}
            />
          </div>
          {isUpdateMode && initialData?.baseUrl && (
            <div className={styles.submitButtonContainer}>
              <Button
                type="button"
                label={"Edit"}
                handleClick={editBtnCallback}
                large={true}
                customClass={styles.submitButton}
              />
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

export default RestaurantPosForm;
